<template>
  <div data-name="marker" />
</template>

<script>
/**
 * Handles point and cluster creation
 * Dispatches popstate event when cluster and points are clicked
 */
import { generateGeoJSON } from './MapHelpers'
import { dispatchCustomEvent } from '@/locations-map/components/common/MapHelpers'

export default {
  name: 'MapMarker',

  props: {
    mapContext: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    locationList: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data() {
    return {
      homeMarker: null,
      mapFeatures: null,
    }
  },

  computed: {
    locationListSorted() {
      return this.locationList.slice().sort((a, b) => a.status.localeCompare(b.status))
    },
  },

  watch: {
    locationListSorted: 'setData',
  },

  mounted() {
    this.setupMapFeatures()
  },

  methods: {
    setupMapFeatures() {
      this.mapFeatures = generateGeoJSON(this.locationListSorted)

      if (!this.mapContext.getSource('enabledLocations')) {
        this.mapContext.addSource('enabledLocations', {
          type: 'geojson',
          data: this.mapFeatures,
          cluster: true,
          clusterMaxZoom: 5, // Max zoom to cluster points on
          clusterRadius: 25, // Radius of each cluster when clustering points (defaults to 50)
        })
      } else {
        this.mapContext.getSource('enabledLocations').setData(this.mapFeatures)
      }

      // Cluster shape
      if (!this.mapContext.getLayer('clusterShape')) {
        this.mapContext.addLayer({
          id: 'clusterShape',
          type: 'circle',
          source: 'enabledLocations',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': '#414141',
            'circle-radius': 14,
          },
        })
      }

      // Cluster text
      if (!this.mapContext.getLayer('clusterText')) {
        this.mapContext.addLayer({
          id: 'clusterText',
          type: 'symbol',
          source: 'enabledLocations',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 12,
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#ffffff',
          },
        })
      }

      const activeLocation = ['==', ['get', 'status'], 'Active']
      const newLocation = ['==', ['get', 'status'], 'Deployment']
      const extendedLocation = ['==', ['get', 'status'], 'Extended']

      // Active locations
      if (!this.mapContext.getLayer('megaportLocations')) {
        this.mapContext.addLayer({
          id: 'megaportLocations',
          type: 'symbol',
          source: 'enabledLocations',
          filter: ['!', ['has', 'point_count']],
          layout: {
            'icon-image': [
              'case',
              activeLocation,
              'redPin',
              newLocation,
              'redPin',
              extendedLocation,
              'plumPin',
              'redPin', // default
            ],
            'icon-size': 0.5,
            'icon-anchor': 'center',
            'icon-allow-overlap': true,
            'icon-offset': [
              'case',
              ['==', ['get', 'icon'], 'redPin'],
              ['literal', [30, 30]],
              ['literal', [0, 0]],
            ],
          },
        })
      }

      // Pin listeners
      this.mapContext.on('mouseenter', 'megaportLocations', this.showCursor)
      this.mapContext.on('mouseleave', 'megaportLocations', this.hideCursor)
      this.mapContext.on('click', 'megaportLocations', this.selectLocation)

      // Cluster listeners
      this.mapContext.on('mouseenter', 'clusterShape', this.showCursor)
      this.mapContext.on('mouseleave', 'clusterShape', this.hideCursor)
      this.mapContext.on('click', 'clusterShape', this.zoomCluster)
    },
    setData() {
      this.mapContext.getSource('enabledLocations').setData(generateGeoJSON(this.locationListSorted))
    },
    showCursor() {
      this.mapContext.getCanvas().style.cursor = 'pointer'
    },
    hideCursor() {
      this.mapContext.getCanvas().style.cursor = ''
    },
    selectLocation(event) {
      const location = this.mapContext.queryRenderedFeatures(event.point, { layers: ['megaportLocations'] })[0]
      dispatchCustomEvent('mp_showLocationPopup', { location: location.properties })
      dispatchCustomEvent('mp_selectLocation', { location: location.properties })
    },
    zoomCluster(event) {
      const features = this.mapContext.queryRenderedFeatures(event.point, { layers: ['clusterShape'] })
      const clusterId = features[0].properties.cluster_id
      this.mapContext.getSource('enabledLocations').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) console.error('@todo handle', err) // @todo handle
        dispatchCustomEvent('mp_zoomToLocation', { center: features[0].geometry.coordinates, zoom: zoom + 3 })
      })
    },
  },
}
</script>
